import React, { useState, useEffect } from "react";
import { Card, TextField } from "@mui/material";
import { SelectProduct } from "../../../types/salon.types";
import OrderItem from "./OrderItem";
import CommonButtons from "src/shared/components/CommonButtons";
import { ToastContainer } from "react-toastify";

interface OrderSectionProps {
  selectedProducts: SelectProduct[];
  discount: string;
  onQuantityChange: (productId: string, newQuantity: number) => void;
  onPriceChange: (productId: string, newPrice: string) => void;
  onRemoveProduct: (productId: string) => void;
  onDiscountChange: (discount: string) => void;
  onConfirmOrder: () => void;
  onCancelOrder: () => void;
}

const OrderSection: React.FC<OrderSectionProps> = ({ selectedProducts, discount, onQuantityChange, onPriceChange, onRemoveProduct, onDiscountChange, onConfirmOrder, onCancelOrder }) => {
  const [localDiscount, setLocalDiscount] = useState(discount);

  const calculateSubtotal = () => {
    if (selectedProducts.length === 0) return 0;
    return selectedProducts.reduce((total, product) => {
      return total + parseFloat(product.amount) * (product.quantity || 1);
    }, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discountValue = parseFloat(discount) || 0;
    if (subtotal === 0) return 0;
    return Math.max(subtotal - discountValue, 0);
  };

  useEffect(() => {
    if (selectedProducts.length === 0 && localDiscount !== "0") {
      setLocalDiscount("0");
      onDiscountChange("0");
    }
  }, [selectedProducts]);

  const handleDiscountChange = (value: string) => {
    setLocalDiscount(value);
    let inputDiscount = parseFloat(value) || 0;
    if (inputDiscount < 0) inputDiscount = 0;
    const subtotal = calculateSubtotal();
    if (inputDiscount <= subtotal) {
      onDiscountChange(inputDiscount.toString());
    }
  };

  const handleDiscountFocus = () => {
    if (localDiscount === "0" || localDiscount === "0.00") {
      setLocalDiscount("");
    }
  };

  const handleDiscountBlur = () => {
    if (localDiscount === "") {
      setLocalDiscount("0");
      onDiscountChange("0");
    }
  };

  return (
    <div className="order-container">
      <div style={{ marginBottom: "10px" }}>
        <Card style={{ padding: "10px", textAlign: "center" }}>
          <h2 style={{ margin: "0", fontSize: "18px" }}>Order Summary</h2>
        </Card>
      </div>

      <div className="selected-list">
        {selectedProducts.map((product) => (
          <OrderItem key={product._id.$oid} product={product} onQuantityChange={onQuantityChange} onPriceChange={onPriceChange} onRemove={onRemoveProduct} />
        ))}
      </div>

      <div className="overall-calculation">
        <div className="inside-calculation">
          <h4 style={{ margin: 0 }}>Subtotal:</h4>
          <p style={{ margin: 0, fontWeight: "bold" }}>{calculateSubtotal().toFixed(2)} QAR</p>
        </div>

        <div className="inside-calculation" id="Discount">
          <h4 style={{ marginTop:"10px" }}>Discount:</h4>
          <TextField
            size="small"
            type="number"
            value={localDiscount}
            onKeyDown={(e) => {
              if (e.key === "-" || e.key === "e") e.preventDefault();
            }}
            onChange={(e) => {
              let value = e.target.value.replace(/-/g, "");
              const inputDiscount = parseFloat(value) || 0;
              const subtotal = calculateSubtotal();

              const validDiscount = inputDiscount >= subtotal ? subtotal - 1 : inputDiscount;
              handleDiscountChange(validDiscount.toString());
            }}
            onFocus={handleDiscountFocus}
            onBlur={handleDiscountBlur}
            inputProps={{
              min: 0,
              max: calculateSubtotal().toFixed(2),
              style: { textAlign: "right" },
            }}
            sx={{ width: "90px", height: "30px" }}
          />
        </div>

        <div className="discount-alignment">
          <h3 style={{marginTop: "10px", }}>Total Price:</h3>
          <p style={{ marginTop: "10px", color: "#007bff" }}>{calculateTotal().toFixed(2)} QAR</p>
        </div>
      </div>

      <div className="flex justify-end gap-3 px-0 py-4 border-t">
        <CommonButtons className="w-40" label="Cancel" variant="outlineDanger" onClick={onCancelOrder} />
        <CommonButtons className="w-40" label="Confirm" onClick={onConfirmOrder} disabled={selectedProducts.length === 0} />
      </div>
      <ToastContainer />
    </div>
  );
};

export default OrderSection;
